import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Stack, Typography,
  Button, TextField, InputAdornment, FormControl, InputLabel, Select, MenuItem,
  Paper, Table, TableContainer, TableHead, TableRow, TableCell, TableBody, IconButton,
  Tooltip, TablePagination, Skeleton, alpha, useTheme, Menu, MenuItem as MenuItemMui,
  ListItemIcon, ListItemText, Alert, Chip
} from '@mui/material';
import {
  Search, Add, UploadFile, MoreVert,
  SwapHoriz, ExitToApp, OpenInNew, Clear, HowToReg,
  TrendingUp, GroupsOutlined, FilterAltOff,
  Mail as MailIcon,
} from '@mui/icons-material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { Enrollment, EnrollmentStatus, EnrollmentListRow as EnrolledRow, SessionClass, SectionInstance, Session } from '../types/sessions';
import { sessionsApi } from '../services/sessions/sessionsApi';
import { classesApi } from '../services/sessions/classesApi';
import { sectionInstancesApi } from '../services/sessions/sectionInstancesApi';
import { enrollmentsApi } from '../services/sessions/enrollmentsApi';
import { useDebounce } from '../hooks/useDebounce';
import { useModulePermissions } from '../hooks/useModulePermissions';
import { useSessionContext } from '../context/SessionContext';

import StatusChip from '../components/common/StatusChip';
import { buildHashedColorMap, getHashedColor } from '../utils/colorUtils';
import EmptyState from '../components/common/EmptyState';
import NewStudentDialog from '../components/enrollments/NewStudentDialog';
import BulkEnrollmentImportDialog from '../components/enrollments/BulkEnrollmentImportDialog';
import EnrollFromAdmissionsDialog from '../components/enrollments/EnrollFromAdmissionsDialog';
import TransferClassDialog from '../components/enrollments/TransferClassDialog';
import ExitEnrollmentDialog from '../components/enrollments/ExitEnrollmentDialog';
import StudentProfileDrawer from '../components/students/StudentProfileDrawer';
import IssueLetterDialog from '../components/letters/IssueLetterDialog';
import PersonCell from '../components/people/PersonCell';

const DEFAULT_ROWS_PER_PAGE = 25;

// Backend defaults a missing status filter to 'enrolled' — history would be
// invisible. Send every status EXCEPT 'transferred_internally' so the table
// shows every row regardless of session lifecycle without doubling up on
// mid-session transfers (source row hidden; the new active row is the truth,
// full lineage is on the student's History drawer).
const ALL_STATUSES: EnrollmentStatus[] = [
  'enrolled', 'transferred_out', 'dropped_out',
  'graduated', 'promoted', 'retained', 'inconclusive'
];

const EnrollmentsPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const { canWrite } = useModulePermissions();
  const { activeSession } = useSessionContext();
  const writable = canWrite('enrollments');

  // ── Filters from URL ─────────────────────────────────────────────────
  const urlSession = searchParams.get('sessionId') ?? '';
  const urlClass   = searchParams.get('classId') ?? '';
  const urlSection = searchParams.get('sectionInstanceId') ?? '';
  const urlSearch  = searchParams.get('search') ?? '';
  const urlGrNumber = searchParams.get('grNumber') ?? '';
  const urlPage    = Math.max(1, parseInt(searchParams.get('page') ?? '1', 10) || 1);

  const [sessions, setSessions] = useState<Session[]>([]);
  const [classes, setClasses] = useState<SessionClass[]>([]);
  const [sections, setSections] = useState<SectionInstance[]>([]);

  const [sessionId, setSessionId] = useState<string>(urlSession);
  const [classId, setClassId]     = useState<string>(urlClass);
  const [sectionId, setSectionId] = useState<string>(urlSection);
  const [searchInput, setSearchInput] = useState<string>(urlSearch);
  const debouncedSearch = useDebounce(searchInput, 350);
  const [grNumberInput, setGrNumberInput] = useState<string>(urlGrNumber);
  const debouncedGrNumber = useDebounce(grNumberInput, 300);
  // page is 0-indexed (TablePagination convention). URL still uses 1-indexed.
  const [page, setPage] = useState<number>(Math.max(0, urlPage - 1));
  const [rowsPerPage, setRowsPerPage] = useState<number>(DEFAULT_ROWS_PER_PAGE);

  const [rows, setRows] = useState<EnrolledRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);

  // Dialogs
  const [newStudentOpen, setNewStudentOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [fromAdmissionsOpen, setFromAdmissionsOpen] = useState(false);
  const [transferEnrollment, setTransferEnrollment] = useState<Enrollment | null>(null);
  const [exitEnrollment, setExitEnrollment]         = useState<EnrolledRow | null>(null);

  // Row action menu
  const [rowMenu, setRowMenu] = useState<{ anchor: HTMLElement; row: EnrolledRow } | null>(null);
  const [issueLetterFor, setIssueLetterFor] = useState<EnrolledRow | null>(null);

  // Student profile drawer
  const [drawerStudentId, setDrawerStudentId] = useState<string | null>(null);

  // Save current view

  // ── Initial loads ────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const [allSessions, allClasses] = await Promise.all([
          sessionsApi.list(),
          classesApi.list()
        ]);
        setSessions(allSessions);
        setClasses(allClasses);
        // Default to active session if URL didn't set one.
        if (!sessionId && activeSession) {
          setSessionId(activeSession._id);
        }
      } catch (err: any) {
        enqueueSnackbar(err?.message || 'Could not load filters', { variant: 'error' });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSession]);

  // ── Load sections when class changes ─────────────────────────────────
  useEffect(() => {
    if (!sessionId || !classId) { setSections([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const secs = await sectionInstancesApi.list({ sessionId, classId });
        if (!cancelled) setSections(secs);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [sessionId, classId]);

  // ── Sync URL whenever filters change ─────────────────────────────────
  useEffect(() => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      if (sessionId) p.set('sessionId', sessionId); else p.delete('sessionId');
      if (classId)   p.set('classId', classId);   else p.delete('classId');
      if (sectionId) p.set('sectionInstanceId', sectionId); else p.delete('sectionInstanceId');
      if (debouncedSearch.trim()) p.set('search', debouncedSearch.trim()); else p.delete('search');
      if (debouncedGrNumber.trim()) p.set('grNumber', debouncedGrNumber.trim()); else p.delete('grNumber');
      // Sweep any stale ?status= from older URLs — no longer a controllable filter.
      p.delete('status');
      // URL stays 1-indexed for human readability.
      if (page > 0) p.set('page', String(page + 1)); else p.delete('page');
      return p;
    }, { replace: true });
  }, [sessionId, classId, sectionId, debouncedSearch, debouncedGrNumber, page, setSearchParams]);

  // ── Load enrollments ─────────────────────────────────────────────────
  const loadEnrollments = useCallback(async () => {
    if (!sessionId) {
      setRows([]); setTotalItems(0); setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data, pagination } = await enrollmentsApi.list({
        sessionId,
        classId: classId || undefined,
        sectionInstanceId: classId && sectionId ? sectionId : (classId ? undefined : undefined),
        status: ALL_STATUSES,
        search: debouncedSearch.trim() || undefined,
        grNumber: debouncedGrNumber.trim() || undefined,
        page: page + 1,    // backend is 1-indexed; UI is 0-indexed
        limit: rowsPerPage
      });

      // Backend now returns each row with a populated `student` sub-doc.
      // No N+1 hydration needed.
      setRows(data as unknown as EnrolledRow[]);
      setTotalItems(pagination?.totalItems ?? data.length);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not load enrollments', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [sessionId, classId, sectionId, debouncedSearch, debouncedGrNumber, page, rowsPerPage, enqueueSnackbar]);

  useEffect(() => { loadEnrollments(); }, [loadEnrollments]);

  // Reset page to 0 when filters change.
  useEffect(() => { setPage(0); }, [sessionId, classId, sectionId, debouncedSearch, debouncedGrNumber]);

  // ── Computed display state ───────────────────────────────────────────
  const selectedSession = useMemo(
    () => sessions.find(s => s._id === sessionId) ?? null,
    [sessions, sessionId]
  );
  const isViewingActive = !!activeSession && sessionId === activeSession._id;
  const showSectionFilter = Boolean(classId) && sections.length > 0;

  const sectionColorMap = useMemo(
    () => buildHashedColorMap(
      rows.map(r => r.section?.name).filter((n): n is string => Boolean(n)),
      theme.palette.mode === 'dark',
    ),
    [rows, theme.palette.mode],
  );

  // ── Handlers ─────────────────────────────────────────────────────────
  const openMenu = (e: React.MouseEvent<HTMLElement>, row: EnrolledRow) =>
    setRowMenu({ anchor: e.currentTarget, row });
  const closeMenu = () => setRowMenu(null);

  const onRefresh = () => loadEnrollments();

  // ── Empty / no-session render ────────────────────────────────────────
  if (!loading && sessions.length === 0) {
    return (
      <Box>
        <EmptyState
          icon={<GroupsOutlined />}
          title="No academic session yet"
          description="Create and activate a session before you can enroll students."
          action={{
            label: 'Go to Academic Sessions',
            onClick: () => navigate('/app/settings/sessions')
          }}
        />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} color="text.primary">Enrollments</Typography>
          <Typography variant="body2" color="text.secondary">
            Students currently sitting in class for the chosen session.
          </Typography>
        </Box>

        {writable && (
          <Stack direction="row" spacing={1}>
            {/* The handover itself is an admissions action (admissions.write). */}
            {canWrite('admissions') && (
              <Tooltip title="Enroll approved admission candidates">
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<HowToReg />}
                    disabled={!isViewingActive}
                    onClick={() => setFromAdmissionsOpen(true)}
                    sx={{ borderRadius: 2 }}
                  >
                    From admissions
                  </Button>
                </span>
              </Tooltip>
            )}
            <Button
              variant="outlined"
              startIcon={<UploadFile />}
              disabled={!isViewingActive}
              onClick={() => setBulkOpen(true)}
              sx={{ borderRadius: 2 }}
            >
              Bulk import
            </Button>
            <Button
              variant="contained"
              disableElevation
              startIcon={<Add />}
              disabled={!isViewingActive}
              onClick={() => setNewStudentOpen(true)}
              sx={{ borderRadius: 2 }}
            >
              Add student
            </Button>
          </Stack>
        )}
      </Stack>

      {/* Filters — matches portal theme: plain white Paper, divider border, no tint */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Session</InputLabel>
            <Select value={sessionId} onChange={(e) => setSessionId(e.target.value)} label="Session">
              {sessions.map(s => (
                <MenuItem key={s._id} value={s._id}>
                  {s.name}
                  {activeSession?._id === s._id && (
                    <Typography component="span" variant="caption" color="success.main" sx={{ ml: 1, fontWeight: 600 }}>
                      · Active
                    </Typography>
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Class</InputLabel>
            <Select value={classId} onChange={(e) => { setClassId(e.target.value); setSectionId(''); }} label="Class">
              <MenuItem value=""><em>All classes</em></MenuItem>
              {classes.map(c => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>

          {showSectionFilter && (
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Section</InputLabel>
              <Select value={sectionId} onChange={(e) => setSectionId(e.target.value)} label="Section">
                <MenuItem value=""><em>All sections</em></MenuItem>
                {sections.map(s => <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>)}
              </Select>
            </FormControl>
          )}

          <Box sx={{ flex: 1 }} />

          <TextField
            size="small"
            placeholder="Search by name"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ minWidth: 240 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" sx={{ color: 'text.disabled' }} />
                  </InputAdornment>
                ),
                endAdornment: searchInput ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchInput('')}
                      aria-label="Clear search"
                      sx={{ p: 0.25 }}
                    >
                      <Clear sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ) : undefined
              }
            }}
          />

          <TextField
            size="small"
            label="GR Number"
            placeholder="e.g. 000042"
            value={grNumberInput}
            onChange={(e) => {
              // Digits-only; cap at 6 so a paste-with-spaces normalises to
              // the canonical GR number. Matches the fee module input exactly.
              const v = e.target.value.replace(/\D/g, '').slice(0, 6);
              setGrNumberInput(v);
            }}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              sx: { fontFamily: 'monospace' },
            }}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6 }}
            sx={{ width: 160 }}
          />

          {(classId || sectionId || searchInput || grNumberInput) && (
            <Tooltip title="Clear class, section, search, and GR number">
              <Button
                size="small"
                variant="text"
                color="inherit"
                startIcon={<FilterAltOff fontSize="small" />}
                onClick={() => {
                  setClassId('');
                  setSectionId('');
                  setSearchInput('');
                  setGrNumberInput('');
                }}
                sx={{ color: 'text.secondary', textTransform: 'none' }}
              >
                Reset
              </Button>
            </Tooltip>
          )}

          {writable && isViewingActive && (
            <Tooltip title="Run promotion for this session">
              <Button
                size="small"
                variant="outlined"
                startIcon={<TrendingUp />}
                onClick={() => navigate(`/app/promotions/new?sourceSessionId=${sessionId}`)}
              >
                Promote
              </Button>
            </Tooltip>
          )}
        </Stack>
      </Paper>

      {!isViewingActive && selectedSession && (
        <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
          You're viewing <strong>{selectedSession.name}</strong> (
          <StatusChip status={{ kind: 'session', value: selectedSession.status }} showIcon={false} sx={{ ml: 0.5, mr: 0.5, verticalAlign: 'middle' }} />
          ). Add / edit actions are disabled — switch to the active session to make changes.
        </Alert>
      )}

      {/* Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden'
        }}
      >
        <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: alpha(theme.palette.text.primary, 0.025) }}>
              <TableCell sx={{ width: 80, fontWeight: 600, color: 'text.secondary' }}>Roll</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Student</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Father</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Class</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Group</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Status</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 6 }).map((__, j) => (
                    <TableCell key={j}><Skeleton variant="text" /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ py: 6 }}>
                  <Stack alignItems="center" spacing={1}>
                    <GroupsOutlined sx={{ fontSize: 36, color: 'text.disabled' }} />
                    <Typography variant="body1" fontWeight={500}>No enrollments match these filters</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360, textAlign: 'center' }}>
                      {classId
                        ? 'Try clearing the section filter, or click "Add student" to enroll.'
                        : 'Pick a class to narrow down, or add a new student.'}
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const className = row.class?.name;
                const sectionName = row.section?.name;
                return (
                  <TableRow
                    key={row._id}
                    hover
                    onClick={() => setDrawerStudentId(String(row.studentId))}
                    sx={{ cursor: 'pointer', '&:last-child td': { borderBottom: 0 } }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={700}>{row.rollNumber}</Typography>
                      {row.rollNumberOverridden && (
                        <Typography variant="caption" color="text.disabled">override</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <PersonCell
                        name={row.student?.name}
                        photoFilename={row.student?.photoFilename}
                        secondary={row.student?.grNumber && (
                          <Box component="span" sx={{ fontFamily: 'monospace', letterSpacing: 0.3 }}>GR {row.student.grNumber}</Box>
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.student?.fatherName ?? '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      {className ? (
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                          <Typography variant="body2" fontWeight={600}>{className}</Typography>
                          {sectionName && (() => {
                            const c = sectionColorMap.get(sectionName);
                            if (!c) return null;
                            return (
                              <Chip
                                label={sectionName}
                                size="small"
                                variant="outlined"
                                sx={{
                                  height: 20,
                                  fontSize: '0.68rem',
                                  fontWeight: 600,
                                  letterSpacing: 0.2,
                                  borderColor: alpha(c.text, 0.35),
                                  color: c.text,
                                  bgcolor: c.bg,
                                  '& .MuiChip-label': { px: 0.9 }
                                }}
                              />
                            );
                          })()}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.group ? (() => {
                        const c = getHashedColor(String(row.group._id ?? row.group.name), theme.palette.mode === 'dark');
                        return (
                          <Chip
                            label={row.group.name}
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 22,
                              fontSize: '0.72rem',
                              fontWeight: 600,
                              letterSpacing: 0.2,
                              borderColor: alpha(c.text, 0.35),
                              color: c.text,
                              bgcolor: c.bg
                            }}
                          />
                        );
                      })() : (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusChip status={{ kind: 'enrollment', value: row.enrollmentStatus }} showIcon={false} />
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Open profile">
                        <IconButton size="small" onClick={() => setDrawerStudentId(String(row.studentId))}>
                          <OpenInNew fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {writable && row.enrollmentStatus === 'enrolled' && (
                        <IconButton size="small" onClick={(e) => openMenu(e, row)}>
                          <MoreVert fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        </TableContainer>

        {/* Pagination — same shape as EmployeesPage */}
        <TablePagination
          component="div"
          count={totalItems}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Paper>

      {/* Row action menu */}
      <Menu
        anchorEl={rowMenu?.anchor ?? null}
        open={Boolean(rowMenu)}
        onClose={closeMenu}
        slotProps={{ paper: { sx: { minWidth: 200, borderRadius: 2 } } }}
      >
        <MenuItemMui onClick={() => {
          if (rowMenu) setIssueLetterFor(rowMenu.row);
          closeMenu();
        }}>
          <ListItemIcon><MailIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Issue letter" secondary="Commendation, warning, fine, etc." />
        </MenuItemMui>
        <MenuItemMui onClick={() => {
          if (rowMenu) setTransferEnrollment(rowMenu.row);
          closeMenu();
        }}>
          <ListItemIcon><SwapHoriz fontSize="small" /></ListItemIcon>
          <ListItemText primary="Transfer class / section" />
        </MenuItemMui>
        <MenuItemMui onClick={() => {
          if (rowMenu) setExitEnrollment(rowMenu.row);
          closeMenu();
        }}>
          <ListItemIcon><ExitToApp fontSize="small" /></ListItemIcon>
          <ListItemText primary="Close enrollment" secondary="Transferred / dropped" />
        </MenuItemMui>
      </Menu>

      <IssueLetterDialog
        open={Boolean(issueLetterFor)}
        onClose={() => setIssueLetterFor(null)}
        studentId={issueLetterFor?.student?._id || ''}
        studentName={issueLetterFor?.student?.name}
      />

      {/* Dialogs */}
      <NewStudentDialog
        open={newStudentOpen}
        activeSession={activeSession}
        classes={classes}
        defaultClassId={classId || undefined}
        defaultSectionInstanceId={sectionId || null}
        onClose={() => setNewStudentOpen(false)}
        onCreated={onRefresh}
      />
      <BulkEnrollmentImportDialog
        open={bulkOpen}
        activeSession={activeSession}
        classes={classes}
        defaultClassId={classId || undefined}
        defaultSectionInstanceId={sectionId || null}
        onClose={() => setBulkOpen(false)}
        onImported={onRefresh}
      />
      <EnrollFromAdmissionsDialog
        open={fromAdmissionsOpen}
        activeSession={activeSession}
        classes={classes}
        defaultClassId={classId || undefined}
        defaultSectionInstanceId={sectionId || null}
        onClose={() => setFromAdmissionsOpen(false)}
        onEnrolled={onRefresh}
      />
      <TransferClassDialog
        open={Boolean(transferEnrollment)}
        enrollment={transferEnrollment}
        classes={classes}
        onClose={() => setTransferEnrollment(null)}
        onTransferred={onRefresh}
      />
      <ExitEnrollmentDialog
        open={Boolean(exitEnrollment)}
        enrollment={exitEnrollment}
        studentName={exitEnrollment?.student?.name}
        onClose={() => setExitEnrollment(null)}
        onExited={onRefresh}
      />

      {/* Student profile drawer — three tabs: Identity, Academic History, Actions. */}
      <StudentProfileDrawer
        open={Boolean(drawerStudentId)}
        studentId={drawerStudentId}
        classes={classes}
        onClose={() => setDrawerStudentId(null)}
        onMutated={onRefresh}
      />
    </Box>
  );
};

export default EnrollmentsPage;
